import {
  Button,
  Form,
  FormItem,
  Input,
  TextArea,
  useForm,
  Message,
} from "@/frontend/components/common";
import { prizeTypeService } from "@/frontend/services";
import { useNavigate } from "react-router-dom";
import type {
  CreatePrizeTypeRequestDTO,
  UpdatePrizeTypeRequestDTO,
  ApiResponse,
  PrizeTypeResponseDTO,
} from "@/shared/dto";
import { useEffect } from "react";

interface PrizeTypeFormValues {
  name: string;
  description?: string;
}

interface PrizeTypeFormProps {
  prizeTypeId?: string;
  initialValues?: Partial<PrizeTypeFormValues>;
}

export function PrizeTypeForm({ prizeTypeId, initialValues }: PrizeTypeFormProps) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!prizeTypeId;

  useEffect(() => {
    if (initialValues && isEditMode) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, isEditMode, form]);

  const handleSubmit = async (values: PrizeTypeFormValues) => {
    try {
      let response: ApiResponse<PrizeTypeResponseDTO>;

      if (isEditMode && prizeTypeId) {
        const updateData: UpdatePrizeTypeRequestDTO = {
          name: values.name,
          description: values.description || undefined,
        };
        response = await prizeTypeService.update(prizeTypeId, updateData);
      } else {
        const createData: CreatePrizeTypeRequestDTO = {
          name: values.name,
          description: values.description || undefined,
        };
        response = await prizeTypeService.create(createData);
      }

      if (response.success && response.data) {
        Message.success({
          content: isEditMode
            ? "Tipo de premiación actualizado correctamente"
            : "Tipo de premiación creado correctamente",
        });
        navigate("/prize-types");
      } else {
        Message.error({
          content:
            response.error ||
            (isEditMode
              ? "Error al actualizar tipo de premiación"
              : "Error al crear tipo de premiación"),
        });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : isEditMode
            ? "Error al actualizar tipo de premiación"
            : "Error al crear tipo de premiación";

      if (
        errorMessage.toLowerCase().includes("nombre") ||
        errorMessage.toLowerCase().includes("name")
      ) {
        form.setFields([{ name: "name", errors: [errorMessage] }]);
      } else {
        Message.error({ content: errorMessage });
      }
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className="w-full"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormItem
          name="name"
          label="Nombre"
          rules={[{ required: true, message: "El nombre es requerido" }]}
        >
          <Input placeholder="Ej: Premio en efectivo, Trofeo" size="large" />
        </FormItem>

        <FormItem name="description" label="Descripción" className="md:col-span-2">
          <TextArea placeholder="Descripción (opcional)" rows={4} size="large" />
        </FormItem>
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <Button type="primary" htmlType="submit" size="large">
          {isEditMode ? "Actualizar" : "Crear"}
        </Button>
        <Button size="large" onClick={() => navigate("/prize-types")}>
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
